import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import {
  getAnnotatorsByState,
  deleteUser,
  getAllUsersIncludingDeleted,
} from "services/userService";
import { transferSamples, getAllAnnotatorsByAdmin } from "services/sampleService";
import { getUserInfo } from "services/authService";
import AppSnackbar from "layouts/notifications/Snackbar";
import { getAllAnnotators } from "services/userService";

export default function DeleteUser({ open, onClose, user, onSuccess }) {
  const loggedUser = getUserInfo();
  const user_id = user ? user.id : null;
  const user_role = user ? user.role : null;
  // Use the same sample count the annotators table shows (distinct rows where this
  // user is annotator1 OR annotator2, across all statuses). Driving off this single
  // value keeps the modal consistent with the table and avoids the per-slot,
  // status-grouped sum that was being string-concatenated (e.g. "054").
  const assignedSamples = Number(user?.assigned_samples ?? 0);

  const [annotatorList, setAnnotatorData] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Toast-snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "",
    message: "",
  });
  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };
  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
      type: "",
      message: "",
    }));
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const userInfo = getUserInfo();
        const adminId = userInfo?.id;
        //fetching annotators
        const data = await getAllAnnotators();
        const annotatorListFilter = data.filter(
          (annotator) => Number(annotator.created_by) === adminId
        );

        setAnnotatorData(annotatorListFilter);
      } catch (error) {
        console.error("Failed to fetch annotators dropdown data", error);
      }
    };

    if (open) {
      fetchDropdownData();
    } else {
      setSelectedValue("");
    }
  }, [open]);

  const onProceed = async () => {
    if (selectedValue) {
      try {
        const result = await transferSamples(user_id, selectedValue);
        if (result.status == "success") {
          showSnackbar("success", "Samples assigned successfully");
          onClose();
          onSuccess?.();
        }
      } catch (error) {
        console.error("Failed to re-assign samples", error);
        showSnackbar("error", "Failed to re-assign samples");
      }
    } else {
      //alert("Please select annotator from dropdown");
      showSnackbar("warning", "Please select annotator from dropdown");
    }
  };

  const onConfirm = async () => {
    await deleteUserAccount();
    onClose();
    onSuccess?.();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDelete = () => {};

  // Other annotators (under the same admin) that samples could be transferred to.
  // The current annotator being deleted is excluded.
  const otherAnnotators = annotatorList.filter((option) => option.id !== user_id);
  const hasTransferTarget = otherAnnotators.length > 0;

  async function deleteUserAccount() {
    try {
      let result = await deleteUser(user_id, loggedUser.id);
      if (result.status == "success") {
        showSnackbar("success", result.message);
      }
    } catch (error) {
      // console.error("Error while deleting annotator:", error);
      showSnackbar("error", "Error while deleting annotator");
      throw error;
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">Delete Annotator</MDTypography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {user_role === "Annotator" && assignedSamples > 0 && !hasTransferTarget ? (
            <DialogContent
              id="no_transfer_dialog"
              sx={{
                backgroundColor: "#f8f9fa",
                p: 2,
              }}
            >
              <DialogContent sx={{ p: 0, mb: 2 }}>
                <MDTypography sx={{ fontSize: "16px", color: "black" }}>
                  This annotator has {assignedSamples} sample{assignedSamples > 1 ? "s" : ""}{" "}
                  assigned, but there is no other annotator available to transfer them to. Please
                  add another annotator before deleting this one.
                </MDTypography>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    color: "#6c757d",
                    borderColor: "#dee2e6",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#ced4da",
                    },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </DialogContent>
          ) : user_role === "Annotator" && assignedSamples > 0 ? (
            <DialogContent id="proceed_dialog" sx={{ backgroundColor: "#f8f9fa", p: 2 }}>
              <DialogContent sx={{ p: 0, mb: 1 }}>
                <MDTypography py={2} sx={{ fontSize: "16px", color: "black" }}>
                  Re-Assign Samples To{" "}
                </MDTypography>
                <Box width={300}>
                  <FormControl fullWidth>
                    {/* <InputLabel id="dropdown-label">Select Annotator</InputLabel> */}
                    <Select
                      value={selectedValue}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      displayEmpty
                      fullWidth
                      sx={{
                        height: 40,
                        px: 1,
                      }}
                      onOpen={() => setIsDropdownOpen(true)}
                      onClose={() => setIsDropdownOpen(false)}
                      IconComponent={() => (
                        <FontAwesomeIcon icon={isDropdownOpen ? faChevronUp : faChevronDown} />
                      )}
                    >
                      <MenuItem value=""> Select Annotator </MenuItem>
                      {annotatorList.map((option, idx) =>
                        option.id !== user_id ? (
                          <MenuItem key={idx} value={option.id}>
                            {option.first_name + " " + option.last_name}
                          </MenuItem>
                        ) : null
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={onProceed}
                  variant="contained"
                  sx={{
                    backgroundColor: "#17a2b8",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#138496",
                    },
                  }}
                >
                  Proceed
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    color: "#6c757d",
                    borderColor: "#dee2e6",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#ced4da",
                    },
                  }}
                >
                  Discard
                </Button>
              </DialogActions>
            </DialogContent>
          ) : user_role === "Annotator" || (user_role === "Admin" && assignedSamples === 0) ? (
            <DialogContent
              id="delete_dialog"
              sx={{
                backgroundColor: "#f8f9fa",
                p: 2,
              }}
            >
              <DialogContent sx={{ p: 0, mb: 2 }}>
                <MDTypography sx={{ fontSize: "16px" }}>
                  Are you sure you want to delete this Annotator?
                </MDTypography>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={onConfirm}
                  variant="contained"
                  sx={{
                    backgroundColor: "#17a2b8",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#138496",
                    },
                  }}
                >
                  Proceed
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    color: "#6c757d",
                    borderColor: "#dee2e6",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#ced4da",
                    },
                  }}
                >
                  Discard
                </Button>
              </DialogActions>
            </DialogContent>
          ) : null}
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

DeleteUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func,
};
